import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Bot, Send, Key, Plus, Copy, Trash2, Terminal, Link2, CheckCircle,
  AlertTriangle, Loader2, Cpu, Server, BookOpen
} from 'lucide-react';
import { toast } from 'sonner';
import * as api from '@/lib/api';

const SESSION_KEY = 'vc_agent_session';
const getSession = () => {
  let s = localStorage.getItem(SESSION_KEY);
  if (!s) { s = `sess_${Date.now()}`; localStorage.setItem(SESSION_KEY, s); }
  return s;
};

const CatalogCard = ({ item }) => (
  <div className="p-4 border rounded-lg space-y-2" data-testid={`catalog-${item.id}`}>
    <div className="flex items-center justify-between gap-2">
      <h3 className="font-medium text-sm">{item.title_pt}</h3>
      <Badge variant="outline" className="text-[10px] font-mono">{item.task_type}</Badge>
    </div>
    <p className="text-xs text-muted-foreground">{item.desc_pt}</p>
    <div className="text-xs">
      <span className="text-muted-foreground">Exemplo: </span>
      <span className="italic">"{item.example_pt}"</span>
    </div>
    <code className="block text-[10px] bg-muted rounded p-2 text-muted-foreground break-all">{item.endpoint}</code>
  </div>
);

export default function AgentAPI() {
  const session = getSession();
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const apiBase = `${backendUrl}/api`;

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const [catalog, setCatalog] = useState([]);
  const [connection, setConnection] = useState({ has_active_key: false, has_download_account: false });
  const [queue, setQueue] = useState([]);

  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyCreated, setNewKeyCreated] = useState(null);
  const [creatingKey, setCreatingKey] = useState(false);

  // Registered agent endpoint (App pushes tasks to the user's agent)
  const [endpoint, setEndpoint] = useState(null);
  const [epName, setEpName] = useState('');
  const [epUrl, setEpUrl] = useState('');
  const [epKey, setEpKey] = useState('');
  const [epSaving, setEpSaving] = useState(false);
  const [epTesting, setEpTesting] = useState(false);

  const loadAll = useCallback(async () => {
    try {
      const [hist, cat, conn, keys, q, ep] = await Promise.all([
        api.getAgentCommandHistory(session),
        api.getAgentCatalog(),
        api.getAgentConnection(),
        api.listCuaApiKeys(),
        api.getAgentQueueTasks(),
        api.getAgentEndpoint(),
      ]);
      setMessages(hist.data.messages || []);
      setCatalog(cat.data.tasks || []);
      setConnection(conn.data || {});
      setApiKeys(keys.data.keys || []);
      setQueue((q.data.tasks || []).slice(0, 6));
      if (ep.data.registered) {
        setEndpoint(ep.data);
        setEpName(ep.data.name || '');
        setEpUrl(ep.data.base_url || '');
      } else {
        setEndpoint(null);
      }
    } catch (e) {
      console.error('agent api load failed', e);
    }
  }, [session]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await api.sendAgentCommand(text, session);
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.reply, action: res.data.action, created: res.data.created }]);
      if (res.data.created) {
        toast.success('Tarefa enviada ao Agente');
        api.getAgentQueueTasks().then((q) => setQueue((q.data.tasks || []).slice(0, 6)));
      }
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erro ao enviar comando';
      setMessages((prev) => [...prev, { role: 'assistant', content: msg, action: 'error' }]);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const handleClearChat = async () => {
    if (!window.confirm('Limpar o histórico do chat?')) return;
    try {
      await api.clearAgentCommandHistory(session);
      setMessages([]);
      toast.success('Histórico limpo');
    } catch { toast.error('Erro ao limpar'); }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    if (!newKeyName.trim()) { toast.error('Digite um nome para a chave'); return; }
    setCreatingKey(true);
    try {
      const res = await api.createCuaApiKey({ name: newKeyName });
      setNewKeyCreated(res.data);
      setNewKeyName('');
      loadAll();
      toast.success('Chave criada!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar chave');
    } finally {
      setCreatingKey(false);
    }
  };

  const handleRevokeKey = async (keyId) => {
    if (!window.confirm('Revogar esta chave?')) return;
    try { await api.revokeCuaApiKey(keyId); loadAll(); toast.success('Chave revogada'); }
    catch { toast.error('Erro ao revogar'); }
  };

  const handleSaveEndpoint = async (e) => {
    e.preventDefault();
    if (!epName.trim() || !epUrl.trim()) { toast.error('Preencha o nome e a URL base'); return; }
    setEpSaving(true);
    try {
      const payload = { name: epName.trim(), base_url: epUrl.trim() };
      if (epKey.trim()) payload.api_key = epKey.trim();
      await api.saveAgentEndpoint(payload);
      setEpKey('');
      toast.success('Agente registrado');
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    } finally { setEpSaving(false); }
  };

  const handleTestEndpoint = async () => {
    setEpTesting(true);
    try {
      const res = await api.testAgentEndpoint();
      if (res.data.reachable) toast.success(`Agente online (${res.data.status})`);
      else toast.error(`Agente offline: ${res.data.status}`);
      loadAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao testar');
    } finally { setEpTesting(false); }
  };

  const handleDeleteEndpoint = async () => {
    if (!window.confirm('Remover o Agente registrado?')) return;
    try {
      await api.deleteAgentEndpoint();
      setEndpoint(null); setEpName(''); setEpUrl(''); setEpKey('');
      toast.success('Agente removido');
      loadAll();
    } catch { toast.error('Erro ao remover'); }
  };

  const copy = (text) => { navigator.clipboard.writeText(text); toast.success('Copiado!'); };

  const envBlock = `VC_API_URL="${apiBase}"\nVC_API_KEY="${newKeyCreated?.key || '<cole-sua-chave-aqui>'}"\nVC_MASTER_PASSWORD="<sua-senha-mestra-do-cofre>"`;

  return (
    <div className="space-y-6 pb-20 md:pb-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Cpu className="w-6 h-6 text-primary" />
            API do Agente
          </h1>
          <p className="text-muted-foreground">
            Converse com o Agente ou pegue a API para conectar seu Desktop Agent. O App delega as tarefas; o Agente executa no seu PC.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={connection.has_active_key ? 'border-green-500/50 text-green-500' : 'border-yellow-500/50 text-yellow-500'} data-testid="conn-key-badge">
            {connection.has_active_key ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
            Chave {connection.has_active_key ? 'ativa' : 'ausente'}
          </Badge>
          <Badge variant="outline" className={connection.has_download_account ? 'border-green-500/50 text-green-500' : 'border-yellow-500/50 text-yellow-500'} data-testid="conn-dl-badge">
            {connection.has_download_account ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
            Conta de Download
          </Badge>
        </div>
      </div>

      <Tabs defaultValue="chat">
        <TabsList className="grid w-full grid-cols-3 max-w-lg">
          <TabsTrigger value="chat" data-testid="agent-tab-chat"><Bot className="w-4 h-4 mr-1" /> Chat</TabsTrigger>
          <TabsTrigger value="connection" data-testid="agent-tab-connection"><Link2 className="w-4 h-4 mr-1" /> Conexão</TabsTrigger>
          <TabsTrigger value="api" data-testid="agent-tab-api"><BookOpen className="w-4 h-4 mr-1" /> API / Tarefas</TabsTrigger>
        </TabsList>

        {/* CHAT */}
        <TabsContent value="chat" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0">
              <div>
                <CardTitle className="text-lg flex items-center gap-2"><Terminal className="w-5 h-5 text-primary" /> Comandar o Agente</CardTitle>
                <CardDescription>Escreva o que quer que o Agente faça. Ex.: "Baixe https://youtu.be/xxxx e gere 5 clips".</CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={handleClearChat} data-testid="agent-chat-clear"><Trash2 className="w-4 h-4" /></Button>
            </CardHeader>
            <CardContent className="space-y-3">
              <div ref={scrollRef} className="h-[360px] overflow-y-auto space-y-3 p-2 rounded-lg bg-muted/30" data-testid="agent-chat-messages">
                {messages.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center text-sm text-muted-foreground gap-2">
                    <Bot className="w-10 h-10 opacity-50" />
                    <p>Nenhuma mensagem ainda. Experimente:</p>
                    <div className="flex flex-wrap gap-2 justify-center max-w-md">
                      {['Baixe https://youtu.be/dQw4w9WgXcQ e gere 3 clips', 'Publique o último clip no YouTube e TikTok', 'Colete os analytics de todas as contas', 'Procure podcasts de negócios com mais de 50 mil views'].map((s) => (
                        <button key={s} onClick={() => setInput(s)} className="text-xs px-2 py-1 rounded-full border border-border hover:bg-muted transition-colors">{s}</button>
                      ))}
                    </div>
                  </div>
                ) : messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm ${m.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-background border border-border'}`}>
                      <p className="whitespace-pre-wrap">{m.content}</p>
                      {m.created && (
                        <div className="mt-1 flex items-center gap-1 text-[11px] opacity-80">
                          <CheckCircle className="w-3 h-3" /> Tarefa criada ({m.created.type})
                        </div>
                      )}
                    </div>
                  </div>
                ))}
                {sending && (
                  <div className="flex justify-start"><div className="rounded-2xl px-3 py-2 bg-background border border-border"><Loader2 className="w-4 h-4 animate-spin" /></div></div>
                )}
              </div>
              <form onSubmit={handleSend} className="flex gap-2">
                <Input
                  placeholder="Diga ao Agente o que fazer..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={sending}
                  data-testid="agent-chat-input"
                />
                <Button type="submit" disabled={sending || !input.trim()} data-testid="agent-chat-send">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Recent delegated tasks */}
          <Card>
            <CardHeader><CardTitle className="text-base flex items-center gap-2"><Server className="w-4 h-4" /> Últimas tarefas enviadas ao Agente</CardTitle></CardHeader>
            <CardContent>
              {queue.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma tarefa ainda.</p>
              ) : (
                <div className="space-y-2">
                  {queue.map((t) => (
                    <div key={t.id} className="flex items-center justify-between p-2 border rounded-lg text-sm" data-testid={`agent-queue-item-${t.id}`}>
                      <span className="font-mono text-xs">{t.task_type}{t.account_label ? ` · ${t.account_label}` : ''}</span>
                      <Badge variant="outline" className="text-xs">{t.status}</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* CONNECTION */}
        <TabsContent value="connection" className="space-y-4 mt-4">
          <Card className="border-primary/30">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Cpu className="w-5 h-5 text-primary" /> Registrar meu Agente</CardTitle>
              <CardDescription>
                Cadastre o endpoint do seu Agente. O App envia as tarefas direto para ele (push) além de disponibilizá-las na fila.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSaveEndpoint} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="ep-name">Nome</Label>
                  <Input id="ep-name" placeholder="Ex.: Meu Agente" value={epName} onChange={(e) => setEpName(e.target.value)} data-testid="endpoint-name-input" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ep-url">URL base</Label>
                  <Input id="ep-url" placeholder="https://meuapp.com/api/agent" value={epUrl} onChange={(e) => setEpUrl(e.target.value)} data-testid="endpoint-url-input" />
                  <p className="text-xs text-muted-foreground">Ex.: https://meuapp.com/api/agent — precisa responder /status, /tasks e /result.</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="ep-key">Chave de API (X-API-Key)</Label>
                  <Input id="ep-key" type="password" placeholder={endpoint?.has_key ? '•••••••• (salva)' : 'Cole a chave do seu Agente'} value={epKey} onChange={(e) => setEpKey(e.target.value)} data-testid="endpoint-key-input" />
                  <p className="text-xs text-muted-foreground">Guardada cifrada; não é mostrada de novo depois de salva.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Button type="submit" disabled={epSaving} data-testid="endpoint-save-btn">
                    {epSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                    <span className="ml-2">{endpoint ? 'Atualizar' : 'Salvar'}</span>
                  </Button>
                  {endpoint && (
                    <>
                      <Button type="button" variant="outline" onClick={handleTestEndpoint} disabled={epTesting} data-testid="endpoint-test-btn">
                        {epTesting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                        <span className="ml-2">Testar conexão</span>
                      </Button>
                      <Button type="button" variant="destructive" onClick={handleDeleteEndpoint} data-testid="endpoint-delete-btn"><Trash2 className="w-4 h-4" /></Button>
                      {endpoint.last_status && (
                        <Badge variant="outline" className={endpoint.last_status === 'online' ? 'border-green-500/50 text-green-500' : 'border-red-500/50 text-red-500'} data-testid="endpoint-status-badge">
                          {endpoint.last_status === 'online' ? <CheckCircle className="w-3 h-3 mr-1" /> : <AlertTriangle className="w-3 h-3 mr-1" />}
                          {endpoint.last_status}
                        </Badge>
                      )}
                    </>
                  )}
                </div>
              </form>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Link2 className="w-5 h-5 text-primary" /> Conectar seu Desktop Agent</CardTitle>
              <CardDescription>Configure estas variáveis no arquivo do agente (viral_clipes_agent.py) e rode <code>python viral_clipes_agent.py loop</code>.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>URL base da API</Label>
                <div className="flex gap-2">
                  <code className="flex-1 p-2 bg-muted rounded text-xs break-all" data-testid="agent-api-base">{apiBase}</code>
                  <Button size="sm" variant="outline" onClick={() => copy(apiBase)}><Copy className="w-4 h-4" /></Button>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Variáveis de ambiente</Label>
                <div className="relative">
                  <pre className="p-3 bg-muted rounded text-xs whitespace-pre-wrap break-all" data-testid="agent-env-block">{envBlock}</pre>
                  <Button size="sm" variant="outline" className="absolute top-2 right-2" onClick={() => copy(envBlock)}><Copy className="w-4 h-4" /></Button>
                </div>
                <p className="text-xs text-muted-foreground">A chave só aparece uma vez, ao criar. VC_MASTER_PASSWORD é a senha mestra do Cofre (opcional, usada para ler cookies da Conta de Download).</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><Key className="w-5 h-5" /> Chaves de API</CardTitle>
              <CardDescription>A chave autentica o Agente. Guarde em local seguro.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {newKeyCreated && (
                <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg space-y-2">
                  <p className="font-medium text-green-500">Chave criada! Copie agora (não será mostrada de novo).</p>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 p-2 bg-muted rounded text-xs break-all" data-testid="new-key-value">{newKeyCreated.key}</code>
                    <Button size="sm" variant="outline" onClick={() => copy(newKeyCreated.key)}><Copy className="w-4 h-4" /></Button>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => setNewKeyCreated(null)}>Fechar</Button>
                </div>
              )}
              <form onSubmit={handleCreateKey} className="flex gap-2">
                <Input placeholder="Nome da chave (ex.: Meu PC)" value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} data-testid="new-key-name-input" />
                <Button type="submit" disabled={creatingKey} data-testid="create-key-btn">
                  {creatingKey ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  <span className="ml-1 hidden sm:inline">Criar</span>
                </Button>
              </form>
              <div className="space-y-2">
                {apiKeys.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Nenhuma chave criada.</p>
                ) : apiKeys.map((k) => (
                  <div key={k.id} className="flex items-center justify-between p-3 border rounded-lg" data-testid={`api-key-${k.id}`}>
                    <div>
                      <p className="font-medium text-sm">{k.name}</p>
                      <code className="text-xs text-muted-foreground">{k.key_prefix}...</code>
                    </div>
                    <Button size="sm" variant="destructive" disabled={!k.is_active} onClick={() => handleRevokeKey(k.id)}>
                      {k.is_active ? 'Revogar' : 'Revogada'}
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API / TASKS */}
        <TabsContent value="api" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2"><BookOpen className="w-5 h-5 text-primary" /> Tarefas que o App delega ao Agente</CardTitle>
              <CardDescription>
                Toda função que antes dependia da API das plataformas pode ser executada manualmente pelo Agente.
                As instruções são enviadas em inglês (bem específicas) para a CUA e exibidas em português aqui.
              </CardDescription>
            </CardHeader>
            <CardContent className="grid gap-3 sm:grid-cols-2">
              {catalog.map((item) => <CatalogCard key={item.id} item={item} />)}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
